import { FC, ReactNode, useState } from 'react';
import { Checkbox, Space, Typography } from 'antd';
import { useFormik } from 'formik';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import Notification from 'components/ui/base/notification/Notification';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import { Declaration } from 'store/declarations/declaration';
import { consultIcs2EnsDeclaration } from 'store/declarations/client';
import { FormFields, FormValues } from './types';
import { identificationValidationSchema } from './validationSchema';
import { FieldControl, FieldLabel, FieldRow } from './styles';

interface Props {
    visible: boolean;
    declaration?: Declaration;
    standalone?: boolean;
    onCancel: () => void;
}

const ConsultEnsModal: FC<Props> = ({ visible, declaration, standalone = false, onCancel }) => {
    const [isConsulting, setIsConsulting] = useState(false);

    const formik = useFormik<FormValues>({
        initialValues: {
            [FormFields.REQUESTER_EORI]: '',
            [FormFields.MRN]: '',
            [FormFields.TRANSPORT_DOCUMENT_TYPE]: undefined,
            [FormFields.TRANSPORT_DOCUMENT_REFERENCE_NUMBER]: '',
            [FormFields.PRE_LOADING_RISK_ASSESSMENT_STATUS]: true,
            [FormFields.NOTIFICATIONS]: true,
        },
        validationSchema: standalone ? identificationValidationSchema : undefined,
        // The confirm button starts disabled until the ENS has been identified.
        validateOnMount: standalone,
        onSubmit: async (values) => {
            if (!standalone && !declaration?.id) return;

            setIsConsulting(true);
            try {
                await consultIcs2EnsDeclaration(declaration?.id, {
                    preLoadingRiskAssessmentStatus: values[FormFields.PRE_LOADING_RISK_ASSESSMENT_STATUS],
                    notifications: values[FormFields.NOTIFICATIONS],
                    ...(standalone && {
                        requesterEori: values[FormFields.REQUESTER_EORI],
                        mrn: values[FormFields.MRN] || undefined,
                        transportDocumentType: values[FormFields.TRANSPORT_DOCUMENT_TYPE],
                        transportDocumentReferenceNumber:
                            values[FormFields.TRANSPORT_DOCUMENT_REFERENCE_NUMBER] || undefined,
                    }),
                });
                formik.resetForm();
                onCancel();
                Notification({
                    type: 'success',
                    messageTitle: 'Consult ENS sent',
                    description: 'Consult ENS has been successfully sent to customs.',
                    placement: 'top',
                });
            } catch (error) {
                Notification({
                    type: 'error',
                    messageTitle: 'Consult ENS failed',
                    description: 'Could not send the Consult ENS request to customs. Please try again.',
                    placement: 'top',
                });
            } finally {
                setIsConsulting(false);
            }
        },
    });

    // Customs is asked for at least one kind of information, otherwise there is nothing to consult.
    const hasRequestedInformation =
        formik.values[FormFields.PRE_LOADING_RISK_ASSESSMENT_STATUS] || formik.values[FormFields.NOTIFICATIONS];

    return (
        <ConfirmationModal
            open={visible}
            width={600}
            destroyOnClose
            title="Consult ENS"
            confirmButtonText="Send to Customs"
            confirmationButtonLoading={isConsulting}
            confirmationButtonDisabled={!hasRequestedInformation || (standalone && !formik.isValid)}
            onConfirm={() => formik.handleSubmit()}
            onCancel={onCancel}
            onClose={onCancel}
            content={
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Typography.Text>
                        Check the current status of any ENS filing with ICS2 - including risk assessment outcome, active
                        Do Not Load orders, any pending referrals or screening requests.
                    </Typography.Text>

                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        {standalone && (
                            <>
                                <ConsultField label="Requester EORI" htmlFor={FormFields.REQUESTER_EORI} required>
                                    <FormInput
                                        condensed
                                        placeholder="Enter EORI"
                                        fieldProps={formik.getFieldProps(FormFields.REQUESTER_EORI)}
                                        fieldMeta={formik.getFieldMeta(FormFields.REQUESTER_EORI)}
                                        fieldHelper={formik.getFieldHelpers(FormFields.REQUESTER_EORI)}
                                    />
                                </ConsultField>

                                <ConsultField label="MRN" htmlFor={FormFields.MRN}>
                                    <FormInput
                                        condensed
                                        placeholder="Enter MRN"
                                        fieldProps={formik.getFieldProps(FormFields.MRN)}
                                        fieldMeta={formik.getFieldMeta(FormFields.MRN)}
                                        fieldHelper={formik.getFieldHelpers(FormFields.MRN)}
                                    />
                                </ConsultField>

                                <ConsultField
                                    label="Transport Document"
                                    htmlFor={`select-id-${FormFields.TRANSPORT_DOCUMENT_TYPE}`}
                                >
                                    <DeclarationSelect
                                        condensed
                                        hideCodelistMenu
                                        codeValidation={[]}
                                        placeholder="Select Document Type"
                                        fieldProps={formik.getFieldProps(FormFields.TRANSPORT_DOCUMENT_TYPE)}
                                        fieldMeta={formik.getFieldMeta(FormFields.TRANSPORT_DOCUMENT_TYPE)}
                                        fieldHelper={formik.getFieldHelpers(FormFields.TRANSPORT_DOCUMENT_TYPE)}
                                        codelistProps={{
                                            codelistName: ICS2CodelistsMap.CL754,
                                            declarationType: DeclarationTypes.ICS2,
                                        }}
                                    />
                                </ConsultField>

                                <ConsultField
                                    label="Transport Document Ref Number"
                                    htmlFor={FormFields.TRANSPORT_DOCUMENT_REFERENCE_NUMBER}
                                >
                                    <FormInput
                                        condensed
                                        placeholder="Enter Document Ref Number"
                                        fieldProps={formik.getFieldProps(
                                            FormFields.TRANSPORT_DOCUMENT_REFERENCE_NUMBER
                                        )}
                                        fieldMeta={formik.getFieldMeta(FormFields.TRANSPORT_DOCUMENT_REFERENCE_NUMBER)}
                                        fieldHelper={formik.getFieldHelpers(
                                            FormFields.TRANSPORT_DOCUMENT_REFERENCE_NUMBER
                                        )}
                                    />
                                </ConsultField>
                            </>
                        )}

                        <ConsultField label="Pre-loading risk assessment status">
                            <Checkbox
                                checked={formik.values[FormFields.PRE_LOADING_RISK_ASSESSMENT_STATUS]}
                                onChange={(event) =>
                                    formik.setFieldValue(
                                        FormFields.PRE_LOADING_RISK_ASSESSMENT_STATUS,
                                        event.target.checked
                                    )
                                }
                            />
                        </ConsultField>

                        <ConsultField label="Notifications">
                            <Checkbox
                                checked={formik.values[FormFields.NOTIFICATIONS]}
                                onChange={(event) =>
                                    formik.setFieldValue(FormFields.NOTIFICATIONS, event.target.checked)
                                }
                            />
                        </ConsultField>
                    </Space>
                </Space>
            }
        />
    );
};

export default ConsultEnsModal;

type ConsultFieldProps = {
    label: string;
    htmlFor?: string;
    required?: boolean;
    children: ReactNode;
};

const ConsultField = ({ label, htmlFor, required, children }: ConsultFieldProps) => (
    <FieldRow>
        <FieldLabel htmlFor={htmlFor}>
            {required && <LabelRequiredSymbol />}
            {label} :
        </FieldLabel>
        <FieldControl>{children}</FieldControl>
    </FieldRow>
);
